import { Avatar, Box, Stack, Typography } from "@mui/material";
import { useState } from "react";

interface Props {
  /** Nombre del comercio tal como lo conoce el comprador. */
  name: string;
  /** URL del logo (la manda el ERP en uiConfig). Puede faltar o estar rota. */
  logoUrl?: string;
  /** Color de marca del comercio; si no viene, el checkout usa el suyo. */
  brandColor?: string;
}

/** Iniciales para el fallback: dos letras como mucho, sin artículos. */
function iniciales(nombre: string): string {
  const palabras = nombre
    .split(/\s+/)
    .filter((p) => p.length > 2 || /^[A-ZÁÉÍÓÚÑ]/.test(p))
    .slice(0, 2);
  return palabras.map((p) => p[0]?.toUpperCase() ?? "").join("") || nombre[0]?.toUpperCase() || "?";
}

/**
 * Quién cobra, arriba de todo. El comprador llega redirigido desde la tienda y
 * lo primero que necesita es reconocer el comercio: por eso la identidad va
 * antes que el monto, y el procesador (Novasis Pay) queda en el pie.
 *
 * El logo es de un tercero y puede no existir o dar 404: en cualquiera de esos
 * casos caemos a las iniciales sobre el color de marca. Una imagen rota acá
 * destruye la confianza que esta cabecera busca construir.
 */
export function MerchantIdentity({ name, logoUrl, brandColor }: Props) {
  const [logoFallo, setLogoFallo] = useState(false);
  const mostrarLogo = !!logoUrl && !logoFallo;

  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      {mostrarLogo ? (
        // Caja blanca con el logo contenido: nunca deformar ni recortar la marca
        // de otro, venga en la proporción que venga.
        <Box
          sx={{
            height: 56,
            maxWidth: 148,
            flexShrink: 0,
            borderRadius: 2,
            bgcolor: "#FFFFFF",
            border: "1px solid rgba(15, 23, 42, 0.10)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            px: 1,
            py: 0.75,
          }}
        >
          <Box
            component="img"
            src={logoUrl}
            alt={name}
            onError={() => setLogoFallo(true)}
            sx={{ maxWidth: 132, maxHeight: "100%", objectFit: "contain", display: "block" }}
          />
        </Box>
      ) : (
        <Avatar
          sx={{
            width: 56,
            height: 56,
            flexShrink: 0,
            borderRadius: 2,
            bgcolor: brandColor ?? "secondary.main",
            color: "#FFFFFF",
            fontWeight: 700,
            fontSize: 18,
          }}
        >
          {iniciales(name)}
        </Avatar>
      )}

      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block", lineHeight: 1.2 }}
        >
          Le pagás a
        </Typography>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, lineHeight: 1.25, overflowWrap: "anywhere" }}
          title={name}
        >
          {name}
        </Typography>
      </Box>
    </Stack>
  );
}
